import { useId, useState } from 'react'
import type { EscalationTarget, Role } from '@lms/shared-types'
import { Select } from '../forms/Select'
import { Button } from '../primitives/Button'
import { Field } from '../primitives/Field'

const ESCALATION_TARGET_LABEL: Record<EscalationTarget, string> = {
  manager: 'Manager',
  hod: 'Head of department',
  director: 'Director',
  exec_director: 'Executive Director',
}

export function escalationTargetsFor(role: Role): ReadonlyArray<EscalationTarget> {
  if (role === 'hod') return ['director', 'exec_director']
  if (role === 'manager') return ['hod', 'director', 'exec_director']
  // HR / director / exec_director rarely escalate from this surface, but we
  // keep the same escalation chain available so the action stays usable.
  return ['hod', 'director', 'exec_director']
}

interface EscalateFormProps {
  viewerRole: Role
  onCancel: () => void
  onConfirm: (target: EscalationTarget) => void
}

export function EscalateForm({
  viewerRole,
  onCancel,
  onConfirm,
}: EscalateFormProps): JSX.Element {
  const targets = escalationTargetsFor(viewerRole)
  const [target, setTarget] = useState<EscalationTarget>(targets[0] ?? 'director')
  const fieldId = useId()
  return (
    <div
      data-testid="escalate-form"
      style={{
        marginTop: 12,
        padding: 12,
        background: 'var(--surface-2)',
        border: '1px solid var(--warning-line)',
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <Field label="Escalate to" htmlFor={fieldId} required>
        <Select
          id={fieldId}
          value={target}
          onChange={(e) => setTarget(e.target.value as EscalationTarget)}
          data-testid="escalate-target"
          options={targets.map((t) => ({ value: t, label: ESCALATION_TARGET_LABEL[t] }))}
        />
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          data-testid="confirm-escalate"
          onClick={() => onConfirm(target)}
        >
          Confirm escalate
        </Button>
      </div>
    </div>
  )
}
